import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ThreeScene } from "./three-scene";
import { Center, Loader, Text, Box } from "@mantine/core";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const initialParams = {
  radius: 3.5,
  detail: 1,
  wobbleStrength: 0.1,
  wobbleSpeed: 1.0,
  rotSpeedX: 0.3,
  rotSpeedY: 0.4,
  rotSpeedZ: 0.2,
  color: "#00C2A9",
  emissive: "#009494",
  emissiveIntensity: .494,
  coreGlowMode: "pulse",
  wobbleEnabled: true,
  rotationEnabled: true,
  metalness: 0.1,
  roughness: 0.1,
  transmission: 1.0,
  thickness: 3.0,
  ior: 1.45,
  opacity: 1.0,
  evolutionSpeed: 2.0,
  lightColor: "#00ffff",
  lightIntensity: 5.0,
  outerRadius: 7.5,
  outerColor: "#ffffff",
  outerEmissive: "#004466",
  outerEmissiveIntensity: 0.05,
  outerOpacity: 0.03,
  outerThickness: 0.5,
  outerRoughness: 0.01,
  outerTransmission: 0.99,
  outerIor: 1.45,
  outerEnvMapIntensity: 2.0,
  outerShellEdgeOpacity: 0.3,
  sceneBgColor: "#001f2f",
  fogDensity: 0.02,
  toneMappingExposure: 1.2,
  bloomStrength: 1.0,
  bloomRadius: 0.8,
  bloomThreshold: 0.2,
  filmGrain: 0.05,
  outerNetColor: "#00ffff",
  outerNetOpacity: 0.1,
  bgPointsColor: "#bfe9ff",
  bgPointsSize: 0.15,
  bgStarsEnabled: true,
  bgStarCount: 600,
  shootingStarsEnabled: true,
  shootingStarFrequency: 1.0,
  glowEnabled: false,
  glowCount: 5,
  glowColor: "#00e5ff",
  glowSize: 34,
  glowSoftness: 0.6,
  glowOpacity: 0.28,
  glowSpeed: 0.4,
  streamColor: "#003300",
  particleColor: "#aaffff",
  packetColor: "#ffffff",
  innerWireframeColor: "#ffffff",
  innerWireframeOpacity: 0.2,
  outerShellEdgeColor: "#88ddff",
  cameraFov: 75,
  cameraDistance: 15,
  cameraAutoRotate: false,
  cameraAutoRotateSpeed: 1.0,
  cameraEnableZoom: true,
  cameraEnablePan: false,
  cameraDamping: true,
  cameraFrameDrag: true,
  cameraLocked: false,
  cameraAzimuth: 0,
  cameraPolar: 1.5708,
  cameraTargetX: 0,
  cameraTargetY: 0,
  cameraTargetZ: 0,
};

export function EmbedPage() {
  const { id } = useParams();
  const [variantParams, setVariantParams] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const convertHexToString = (value) => {
      if (typeof value === 'number') {
        return '#' + value.toString(16).padStart(6, '0');
      }
      return value;
    };

    const loadVariant = async () => {
      if (!id) {
        setError("No variant ID provided");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/api/variants/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch variant');
        }
        const variantData = await response.json();

        if (!variantData.isPublic) {
          throw new Error('This variant is not public');
        }

        const loadedParams = variantData.params;

        const colorFields = ['color', 'emissive', 'lightColor', 'outerColor', 'outerEmissive', 'sceneBgColor', 'outerNetColor', 'bgPointsColor', 'streamColor', 'particleColor', 'packetColor', 'innerWireframeColor', 'outerShellEdgeColor'];
        colorFields.forEach(field => {
          if (loadedParams[field] !== undefined) {
            loadedParams[field] = convertHexToString(loadedParams[field]);
          }
        });

        const finalParams = { ...initialParams, ...loadedParams };
        setVariantParams(finalParams);
        setIsLoading(false);
      } catch (e) {
        console.error("Failed to load variant", e);
        setError(e.message);
        setIsLoading(false);
      }
    };

    loadVariant();
  }, [id]);

  if (isLoading) {
    return (
      <Center h="100vh" bg="#001f2f">
        <Loader color="cyan" />
        <Text ml="xs" c="cyan" ff="monospace">Loading...</Text>
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh" bg="#001f2f">
        <Text c="red" ff="monospace">{error}</Text>
      </Center>
    );
  }

  if (!variantParams) {
    return (
      <Center h="100vh" bg="#001f2f">
        <Text c="cyan" ff="monospace">Variant not found</Text>
      </Center>
    );
  }

  return (
    <Box w="100%" h="100vh" pos="relative" style={{ overflow: 'hidden', margin: 0, padding: 0 }}>
      <ThreeScene
        initialParams={variantParams}
        guiContainerRef={null}
        isEmbedMode={true}
      />
    </Box>
  );
}